import React, { useState, useEffect } from 'react';
import { Lock, ArrowLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WeddingPasswordPrompt.css';

const API_URL = 'https://image-upload-api-gamma.vercel.app/api/images';

function WeddingPasswordPrompt({ weddingName, onSuccess }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('landing'); // 'landing' or 'password'
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState(null);

  useEffect(() => {
    // Fetch generic cover image for landing page
    const fetchCoverImage = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/metadata`);
        if (data && data.images) {
          // Look for an image in "weddings/{weddingName}/landing" OR just any cover image for this wedding
          const weddingCover = data.images.find(img =>
            img.category === `weddings/${weddingName}/landing` ||
            (img.isCover && img.weddingName === weddingName)
          );

          if (weddingCover) {
            setCoverImage(weddingCover.url);
          }
        }
      } catch (err) {
        console.error('Failed to fetch cover image:', err);
      }
    };

    fetchCoverImage();
  }, [weddingName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/verify-wedding-password`, {
        weddingName,
        password: password.trim()
      });

      if (response.data.valid) {
        sessionStorage.setItem(`wedding_${weddingName}`, password.trim());
        onSuccess();
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch (err) {
      console.error('Password verification error:', err);
      setError('Failed to verify password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPassword(value);
    setError('');
  };

  if (step === 'landing') {
    return (
      <div
        className="landing-container"
        style={coverImage ? { backgroundImage: `url(${coverImage})` } : {}}
      >
        <div className="landing-overlay" />
        <div className="landing-content">
          <div className="logo-container" onClick={() => navigate('/')}>
            <img src="https://ik.imagekit.io/p3xaqnkci/tr:f-auto,q-auto,w-1200/tagteam/logo.png" alt="TagTeam Studios" className="tagteam-logo-img" />
          </div>

          <div className="landing-text">
            <h2>Welcome to the Gallery of</h2>
            <h1>{weddingName}</h1>
            <button
              className="enter-gallery-btn"
              onClick={() => setStep('password')}
            >
              Enter Gallery
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-prompt-container">
      <button onClick={() => setStep('landing')} className="back-btn-password">
        <ArrowLeft size={20} />
        Back
      </button>

      <div className="password-prompt-box">
        <div className="password-prompt-header">
          <Lock size={48} />
          <h2>{weddingName}</h2>
          <p>This wedding gallery is password protected</p>
        </div>

        <form onSubmit={handleSubmit} className="password-prompt-form">
          <div className="password-input-group">
            <label>Enter 4-Digit Password</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={password}
              onChange={handlePasswordChange}
              placeholder="0000"
              maxLength={4}
              required
              autoFocus
              className="password-input-visible"
            />
          </div>

          {error && <div className="password-error">{error}</div>}

          <button
            type="submit"
            className="password-submit-btn"
            disabled={loading || password.length !== 4}
          >
            {loading ? 'Verifying...' : 'Access Gallery'}
          </button>

          <p className="password-hint">
            Contact TagTeam Studios if you don't have the password
          </p>
        </form>
      </div>
    </div>
  );
}

export default WeddingPasswordPrompt;